// VulnerabilityPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Banner from '../components/Banner'; // Import Banner component
import InfoBanner from '../components/InfoBanner';
import { useParams } from 'react-router-dom';
import PageNotFound from '../pages/PageNotFound';
import Tooltip from '../components/tooltip'; // Import Tooltip component
import ExternalLinksBanner from '../components/ExternalLinksBanner';
import ExploitationInsightsTable from '../components/ExploitationInsightsTable';


import { appUrl } from '../properties/properties';

const VulnerabilityPage = () => {
  const { id } = useParams();
  const [vulnerability, setVulnerability] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [kevEntries, setKevEntries] = useState([]);
  const [honeypotObservations, setHoneypotObservations] = useState([]);

  useEffect(() => {
    const fetchVulnerability = async () => {
      try {
        const response = await fetch(`${appUrl}/enisaid?id=${id}`);
        // 404 since ENISASUP-924; 204 is what the API sent before, kept so this page
        // works whichever of the two is deployed first.
        if (response.status === 404 || response.status === 204) {
            setNotFound(true);
        } else if (!response.ok) {
            // An error body is not a vulnerability; rendering it as one crashes the page.
            throw new Error(`Unexpected status ${response.status}`);
        } else {
            const data = await response.json();
            setVulnerability(data);
        }
      } catch (error) {
        console.error('Error fetching vulnerability details:', error);
      }
    };

    fetchVulnerability();
  }, [id]);

  // Fetch KEV entries + Shadowserver honeypot observations when vulnerability loads.
  // The two signals are distinct: KEV is a curated list (CISA / EUKEV);
  // Honeypot data is Shadowserver's live telemetry.
  useEffect(() => {
    if (!vulnerability || !vulnerability.enisaIdVulnerability) return;

    const fetchKevEntries = async (primaryCveId) => {
      try {
        const response = await fetch(`${appUrl}/kevEntries?cveId=${primaryCveId}`);
        if (response.ok) {
          const data = await response.json();
          setKevEntries(data || []);
        }
      } catch (error) {
        console.error('Error fetching KEV entries:', error);
      }
    };

    const fetchHoneypotObservations = async (primaryCveId) => {
      try {
        const response = await fetch(`${appUrl}/honeypotObservations?cveId=${primaryCveId}`);
        if (response.ok) {
          const data = await response.json();
          setHoneypotObservations(data || []);
        }
      } catch (error) {
        console.error('Error fetching honeypot observations:', error);
      }
    };

    const cveIds = vulnerability.enisaIdVulnerability
      .map(v => v.vulnerability.id)
      .filter(vid => vid && vid.startsWith('CVE-'));

    if (cveIds.length === 0) return;
    fetchKevEntries(cveIds[0]);
    fetchHoneypotObservations(cveIds[0]);
  }, [vulnerability]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    let month = date.getMonth() + 1;
    month = month < 10 ? `0${month}` : month;
    let day = date.getDate();
    day = day < 10 ? `0${day}` : day;

    let hour = date.getHours();
    hour = hour < 10 ? `0${hour}` : hour;
    let minute = date.getMinutes();
    minute = minute < 10 ? `0${minute}` : minute;

    return `${year}-${month}-${day} ${hour}:${minute}`;
  };

  const renderReferences = () => {
    const references = vulnerability.references.split('\n');
    return references.map((reference, index) => (
      <li key={index}>
        <a href={reference} target="_blank" rel="noopener">{reference}</a>
      </li>
    ));
  };

  const renderVulnerabilities = () => {    
    return vulnerability.enisaIdVulnerability.map((vulnItem, index) => (
      <li key={index}>
        <a href={manageLinks(vulnItem.vulnerability.id)} target='_blank'>{vulnItem.vulnerability.id}</a>
        {vulnItem.vulnerability.status === 'REJECTED' && (
          <span className='red'> (Rejected)</span>
        )}
      </li>
    ));
  };

  const renderAdvisories = () => {
    return vulnerability.enisaIdAdvisory.map((advisoryItem, index) => (
      <li key={index}>
        <p><a href={`/advisory/${advisoryItem.advisory.id}`} target="_blank" rel="noopener">{advisoryItem.advisory.id}</a> {advisoryItem.advisory.source ? '('+(advisoryItem.advisory.source.name)+')' : ''}</p>
      </li>
    ));
  };
  const renderProductInfo = () => {
    return vulnerability.enisaIdProduct.map((product) => (
      <tr key={product.id}>
        <td data-header="Vendor" className='vendor'>{product.product && product.product.vendor && product.product.vendor.name}</td>
        <td data-header="Product" className='product'>{product.product.name}</td>
        <td data-header="Version" className='version'>{product.product_version}</td>
      </tr>
    ));
  }

  // Vendors that appear in enisaIdVendor but NOT as the vendor of any
  // enisaIdProduct row. These come from CVE affected[] entries that recorded
  // a vendor without a product ("we know X is affected, product unknown").
  // Surfaced separately so they don't reappear next to the merged product
  // table — that would duplicate vendors already listed there.
  const getOtherVendors = () => {
    if (!vulnerability.enisaIdVendor) return [];
    const productVendorNames = new Set(
      (vulnerability.enisaIdProduct || [])
        .map((p) => p.product && p.product.vendor && p.product.vendor.name)
        .filter(Boolean)
        .map((n) => n.toLowerCase())
    );
    const seen = new Set();
    const result = [];
    vulnerability.enisaIdVendor.forEach((v) => {
      const name = v.vendor && v.vendor.name;
      if (!name) return;
      const key = name.toLowerCase();
      if (productVendorNames.has(key) || seen.has(key)) return;
      seen.add(key);
      result.push(name);
    });
    return result;
  };

  const renderCVSS = (vulnerability) =>{
    if (vulnerability.baseScore && vulnerability.baseScore !== -1 && vulnerability.baseScore !==0){
      return <p className='score-data relative full-width'>
                <strong>
                    <span>CVSS Base Score: <span>{vulnerability.baseScore}</span> (v{vulnerability.baseScoreVersion})</span>
                </strong>
                <span className="cvss-vector">
                  {vulnerability.baseScoreVector && vulnerability.baseScoreVersion && vulnerability.baseScoreVersion !== "2.0" && (
                    <a
                      href={`https://www.first.org/cvss/calculator/${vulnerability.baseScoreVersion}#${vulnerability.baseScoreVector}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      ({`${vulnerability.baseScoreVector}`})
                    </a>
                  )}
                </span>
                <Tooltip text="The Common Vulnerability Scoring System (CVSS) provides a way to capture the principal characteristics of a vulnerability and produce a numerical score reflecting its severity."><span className='info'></span> </Tooltip>
              </p>
    }else{
      return <p className='score-data relative full-width'><span></span></p>
    }
  }
  const manageLinks = (externalLink) =>{
    externalLink = externalLink.toUpperCase();

    if (externalLink.includes("CVE-")){
        externalLink = 'https://www.cve.org/CVERecord?id=' + externalLink;
    } else if (externalLink.includes("GHSA-")){
        externalLink = 'https://github.com/advisories/' + externalLink;
    }else if (externalLink.includes("GSD-")){
        externalLink = 'https://gsd.id/' + externalLink;
    }else if (externalLink.includes("PYSEC-")){
        externalLink = 'https://github.com/pypa/advisory-database';
    }else if (externalLink.includes("MAL-")){
        externalLink = 'https://osv.dev/list';
    }
    return externalLink;
  }

  useEffect(() => {
    if (vulnerability) {
      document.title = `${vulnerability.id} | EUVD`;
    } else {
      document.title = "EUVD";
    }
  }, [vulnerability]);
  
  return (
    <>
      <Header />
      <InfoBanner />
      {notFound ? (
        <PageNotFound />
      ) : (
        vulnerability && (
          <main>
            <ExternalLinksBanner />
            <section className="banner">
              <nav className="breadcrumbs container-1200">
                <ul>
                  <li><a href="/homepage">Home</a></li>
                  <li><a href="/search">Vulnerability List</a></li>
                  <li><a href="">{vulnerability.id}</a></li>
                </ul>
              </nav>
            </section>

            <section className="container-1200 container-up" id='enisaDetailPage'>
              <h1 className="hidden">ENISA Vulnerability Database</h1>

              <div className="vulnerability-item detail">
                <div className="title-wrapper">
                  <h2>{vulnerability.id}</h2>
                  <a className="btn-primary back" href="/search">Back to the vulnerability search</a>
                </div>

                <div className="top-item-container detail-section-wrapper">
                    <section className="score-container score-value">
                      <h3 className="underlined-title">Severity</h3>
                      <div className={`full-width score ${vulnerability.baseScore && 'score-' + Math.round(vulnerability.baseScore)}`}>
                       {renderCVSS(vulnerability)}
                      </div>

                    </section>
                      <section className='alternative-ids-wrapper'>
                        <h3 className="underlined-title">Alternative IDs</h3>
                        <ul className="section-container">
                          {renderVulnerabilities()}
                        </ul>
                      </section>
                    
                </div>
                  
                <ExploitationInsightsTable
                    kevEntries={kevEntries}
                    honeypotObservations={honeypotObservations}
                    epss={vulnerability.epss}
                />

                <section className="summary-wrapper">
                    <h3 className="underlined-title">Summary</h3>
                    <p>{vulnerability.description}</p>
                </section>
                
                <section className="affected-products-wrapper">
                    <h3 className="underlined-title">Affected Product</h3>
                    {(() => {
                      const hasProducts = vulnerability.enisaIdProduct && vulnerability.enisaIdProduct.length > 0;
                      const otherVendors = getOtherVendors();
                      if (!hasProducts && otherVendors.length === 0) {
                        return <p className="info">Affected product information not available</p>;
                      }
                      return (
                        <div className='info-wrapper'>
                          {hasProducts && (
                            <div className="info product-info">
                              <table>
                                <thead>
                                  <tr>
                                    <th className="label vendor">Vendor:</th>
                                    <th className="label product">Product:</th>
                                    <th className="label version">Version:</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {renderProductInfo()}
                                </tbody>
                              </table>
                            </div>
                          )}
                          {otherVendors.length > 0 && (
                            <div className="info other-vendors">
                              <p className="label">Other affected vendors:
                                <Tooltip text="Vendors recorded as affected without specific product details."><span className='info'></span> </Tooltip>
                              </p>
                              <ul>
                                {otherVendors.map((name) => (
                                  <li key={name}>{name}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                </section>

                <div className="id-information-wrapper">
                  <section>
                    <h3 className="underlined-title">Advisory IDs <span className='counter'>{renderAdvisories().length}</span></h3>
                    <ul className="section-container">
                      {renderAdvisories()}
                    </ul>
                  </section>
                  <section className='vulnerability-information-wrapper'>
                    <h3 className="underlined-title">Vulnerability information</h3>
                    <div className="info relative">
                        {vulnerability.datePublished && (
                          <p className="label published">Published: <span>{formatDate(vulnerability.datePublished)}</span></p>
                        )}
                        {vulnerability.dateUpdated && (
                          <p className="label updated">Updated: <span>{formatDate(vulnerability.dateUpdated)}</span></p>
                        )}
                        {vulnerability.assigner && (
                            <p className="label csirt tooltip-left">Assigner:
                              <Tooltip text="The identifier assigning CVE Numbering Authority (CNA)."><span className='info'></span> </Tooltip>
                              <span>{vulnerability.assigner}</span>
                            </p>
                        )}
                    </div>
                  </section>
                </div>
                <section className="references-wrapper">
                  <h3 className="underlined-title">References</h3>
                  {vulnerability.references ? (
                      <>
                        <ul className="section-container">
                          {renderReferences()}
                        </ul>
                      </>
                    ) : (
                      <p className="info">Not available</p>
                    )}
                 
              </section>
            </div>
          </section>
          <Banner />
        </main>
        )
      )}
      <Footer />
    </>
  );
};

export default VulnerabilityPage;
